import React, { Component } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Link as MuiLink,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { websiteAddress, isUndeclaredGoogleSitesAddress } from '../lib/websiteAddress';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import {
  getSites,
  analyze,
  isAnalysisRunning,
  SESSION_EXPIRED,
  PAGE_SIZE
} from '../services/hostingDetectedService';
import {
  hostingFreshness,
  emptyRatio,
  breakAge,
  STALE_AFTER_DAYS,
  OLD_BREAK_AFTER_DAYS
} from '../lib/hostingFreshness';
import {
  googleSitesActivity,
  isActivityFilterAvailable,
  isRowDimmed,
  totalLabel,
  purgeHalt,
  canPurgeFromRow,
  clampPage
} from '../lib/googleSitesActivity';
import { pageCountFor } from './StatViews';
import WebsitePurgeDialog from './WebsitePurgeDialog';

// Écran « Google Sites » — suivi du parc (google-sites-install_P1, B4_01, D51).
//
// Pas un outil de relance (D51) : une liste, une recherche, un bouton qui lance UN lot d'analyse —
// et, depuis B4_02 objectif 3, la SUPPRESSION INTÉGRALE d'un site inactif, ligne par ligne (le
// volume passe par le script `purge_websites.js`, jamais par une sélection multiple). Elle lit le
// marqueur `Website.hosting_detected` posé par l'API (B1_02, contrat C7) — jamais `threads`.
//
// Ce que l'écran doit dire pour ne pas mentir sur sa propre sémantique :
//  - la liste est un STOCK CUMULÉ (D63) : un site réparé n'en sort jamais ; seule « Dernière
//    cassée » distingue une panne vive d'une cicatrice → puce « ancien » au-delà de 30 j ; le GRISAGE
//    de la ligne, lui, suit l'activité réelle (`isRowDimmed`, amende D67 — décision Mathias
//    2026-09-20, 🛑1) ;
//  - la date d'en-tête est celle de la dernière analyse COMPLÈTE du parc (D44), en 3 états
//    (`unknown` champ absent / `never` null / date) — jamais celle d'un passage partiel ;
//  - les deux `remaining` (GET en tête, POST après le clic) ne mesurent PAS la même chose :
//    libellés distincts, toujours (`hosting_detected_controller.js:69` vs `hostingDetection.js:354`) ;
//  - « Dernière cassée » n'est PAS un critère d'activité (B4_02) : elle mesure des chargements du
//    widget, robots compris. L'activité se lit sur le DERNIER COMMENTAIRE (`last_comment_at`), et
//    le filtre Tous / Actifs / Inactifs est appliqué PAR LE SERVEUR sur tout le parc — il repart
//    donc à CHAQUE requête (5 appels de `fetchPage`), et tout changement de filtre remet la page à 1.
//  - la suppression ouvre la MÊME fenêtre que la fiche du site (`WebsitePurgeDialog`, montée UNE
//    fois hors des lignes). Le moteur passe le site à la corbeille dès le LANCEMENT : il sort de
//    la liste servie à cet instant, d'où le rechargement à la FERMETURE de la fenêtre en plus de
//    la fin de suppression (la fenêtre n'a pas de rappel de lancement). Une suppression arrêtée ou
//    interrompue laisse le site à la corbeille : l'API le sert quand même (`purge.status`), puce
//    sur la ligne, et le bouton rouvre le suivi pour reprendre. ⛔ Aucun verrou « une suppression
//    tourne » ici : c'est le 409 de la fenêtre qui nomme le site qui tient la place.
//
// Aucune adresse email nulle part (D30) : écran sans journal de consultation.
// Le tableau n'est jamais remplacé par un spinner (piège Agents.jsx B5_01) : LinearProgress fine.

// ── Helpers purs (exportés pour `src/tests/hostingDetected.test.js`, D38) ────────────────

// `data-state` du conteneur : ancre des recettes `gc-recette-ui` (un compteur rendu avant la
// réponse n'est pas un oracle — super-admin/CLAUDE.md, Pièges).
export const screenState = ({ isLoading, error, sites, loaded = true }) => {
  if (error) return 'error';
  if (isLoading || !loaded) return 'loading';
  return sites && sites.length > 0 ? 'ready' : 'empty';
};

// Le `remaining` du GET est codé en dur `onlyNew:true` côté API : il compte les sites du PARC
// (~19 000, tous hébergeurs) jamais analysés — pas des sites Google Sites. ⛔ Jamais « restants » nu.
export const HEADER_REMAINING_LABEL = 'Sites du parc jamais analysés';

// Le `remaining` du POST dépend du mode demandé (`countRemaining({onlyNew, startedAt})`).
export const remainingLabel = (onlyNew) =>
  onlyNew
    ? 'sites jamais analysés restants'
    : 'à rejouer (constats plus anciens que ce passage)';

// Ligne vide du tableau : le qualificatif suit le filtre d'activité appliqué.
const EMPTY_QUALIFIER = { all: 'concerné', active: 'actif', inactive: 'inactif' };

const toCount = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const ucfirst = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str);

// Résumé d'un lot, tel que servi. `analyzed` n'est incrémenté qu'en cas de SUCCÈS
// (`hostingDetection.js:330`) ; un échec va dans `failed` et le site n'est pas marqué analysé.
export const analyzeSummary = (result, onlyNew) => {
  const r = result || {};
  return {
    analyzed: toCount(r.analyzed),
    detected: toCount(r.detected),
    failed: toCount(r.failed),
    remaining: r.remaining === undefined || r.remaining === null ? null : toCount(r.remaining),
    failedIds: Array.isArray(r.failed_website_ids) ? r.failed_website_ids.map(String) : [],
    remainingLabel: remainingLabel(onlyNew)
  };
};

export const describeLoadError = (err) => {
  if (!err) return null;
  if (err.code === SESSION_EXPIRED) {
    return { severity: 'warning', message: 'Session expirée : recharge la page pour te reconnecter.' };
  }
  return { severity: 'error', message: `Chargement impossible (${shortMessage(err)}).` };
};

// 409 = « une analyse est déjà en cours » (autre appel ou script de rattrapage) — lu dans le
// PRÉFIXE du message levé par http.js, jamais dans un corps que la garde ne voit pas.
export const describeAnalyzeError = (err) => {
  if (!err) return null;
  if (isAnalysisRunning(err)) {
    return {
      severity: 'info',
      code: 'analysis_running',
      message: 'Une analyse est déjà en cours (autre appel ou script de rattrapage). Réessaie dans quelques instants.'
    };
  }
  if (err.code === SESSION_EXPIRED) {
    return {
      severity: 'warning',
      code: SESSION_EXPIRED,
      message: 'Session expirée : recharge la page pour te reconnecter. Aucune analyse n’a été lancée.'
    };
  }
  return { severity: 'error', code: 'error', message: `Analyse impossible (${shortMessage(err)}).` };
};

// http.js réinjecte le corps INTÉGRAL de la réponse dans le message (`HTTP <code>: <texte>`) :
// sur un 502 de proxy, c'est une page HTML. React échappe (pas d'XSS), mais on tronque.
const shortMessage = (err, max = 160) => {
  const m = (err && err.message) || 'erreur';
  return m.length > max ? `${m.slice(0, max)}…` : m;
};

const fmtDate = (value, withTime = false) => {
  if (!value) return '—';
  const m = moment(value);
  if (!m.isValid()) return '—';
  return m.format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY');
};

class HostingDetected extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sites: [],
      total: 0,
      page: 1,
      query: '',
      appliedQuery: '',
      // Filtre d'activité APPLIQUÉ (valeurs d'API `all|active|inactive`) et seuil servi par l'API.
      // `inactiveAfterDays` absent = API d'avant B4_02 : elle ignore `activity` → sélecteur désactivé.
      activity: 'all',
      inactiveAfterDays: undefined,
      // `undefined` tant que rien n'a été chargé : distinct de `null` (« aucune analyse complète »).
      lastFullAnalysisAt: undefined,
      remaining: undefined,
      loaded: false,
      isLoading: false,
      error: null,
      onlyNew: true,
      analyzing: false,
      analyzeResult: null,
      analyzeMode: true,
      analyzeError: null,
      // Site dont la fenêtre de suppression est ouverte (`null` = fermée) — gabarit `Website.js`.
      purgeWebsiteId: null
    };
    this.fetchPage = this.fetchPage.bind(this);
    this.reloadAfterPurge = this.reloadAfterPurge.bind(this);
    this.closePurge = this.closePurge.bind(this);
    this.onSearchSubmit = this.onSearchSubmit.bind(this);
    this.onPageChange = this.onPageChange.bind(this);
    this.onActivityChange = this.onActivityChange.bind(this);
    this.onAnalyze = this.onAnalyze.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchPage(1, '', 'all');
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // ⛔ `activity` est un paramètre OBLIGATOIRE de chaque appel : un appel qui l'oublie recharge la
  // liste NON filtrée pendant que le sélecteur affiche « Inactifs » — et aucun test d'écran ne le
  // voit (D38).
  fetchPage(page, appliedQuery, activity) {
    this.setState({ isLoading: true, error: null });
    getSites({ q: appliedQuery, page, activity })
      .then((result) => {
        if (!this._isMounted) return;
        // Une API qui ne sert pas le seuil a IGNORÉ `activity` : la liste reçue n'est pas filtrée,
        // on ne la présente donc pas comme telle.
        const served = isActivityFilterAvailable(result.inactive_after_days);
        // Le total a pu BAISSER (site supprimé) : la page demandée n'existe peut-être plus. On
        // repart sur la dernière page existante — une seule fois, elle est dans les bornes.
        const lastValid = clampPage(page, toCount(result.total), PAGE_SIZE);
        if (lastValid !== page) {
          this.fetchPage(lastValid, appliedQuery, activity);
          return;
        }
        this.setState({
          sites: Array.isArray(result.sites) ? result.sites : [],
          total: toCount(result.total),
          page,
          appliedQuery,
          activity: served ? activity : 'all',
          inactiveAfterDays: result.inactive_after_days,
          // Champ ABSENT → reste `undefined` (état `unknown`), `null` → `never` : jamais fondus.
          lastFullAnalysisAt: result.last_full_analysis_at,
          remaining: result.remaining,
          loaded: true,
          isLoading: false
        });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ isLoading: false, error: err || new Error('error') });
      });
  }

  onSearchSubmit(e) {
    e.preventDefault();
    // Le résumé du dernier lot décrit un écran qui n'est plus celui-ci : on l'efface.
    this.setState({ analyzeResult: null, analyzeError: null });
    this.fetchPage(1, (this.state.query || '').trim(), this.state.activity);
  }

  onPageChange(e, value) {
    this.setState({ analyzeResult: null, analyzeError: null });
    this.fetchPage(value, this.state.appliedQuery, this.state.activity);
  }

  // ToggleButtonGroup exclusif : un clic sur le bouton déjà actif rend `null` — on l'ignore (il
  // y a toujours un mode). Changer de filtre change le nombre de pages → retour page 1.
  onActivityChange(e, value) {
    if (!value || value === this.state.activity) return;
    this.setState({ analyzeResult: null, analyzeError: null });
    this.fetchPage(1, this.state.appliedQuery, value);
  }

  onAnalyze() {
    const { onlyNew } = this.state;
    this.setState({ analyzing: true, analyzeError: null, analyzeResult: null, analyzeMode: onlyNew });
    analyze({ onlyNew })
      .then((result) => {
        if (!this._isMounted) return;
        this.setState({ analyzing: false, analyzeResult: result });
        // Le lot a pu marquer des sites : on recharge la page COURANTE (celle affichée à la fin
        // du lot, pas celle capturée au clic — l'admin a pu paginer pendant les 25 s), recherche
        // ET filtre d'activité conservés, pour rafraîchir liste/total/en-tête — sans démonter le tableau.
        this.fetchPage(this.state.page, this.state.appliedQuery, this.state.activity);
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ analyzing: false, analyzeError: err || new Error('error') });
      });
  }

  // Après une suppression lancée, arrêtée ou terminée : page COURANTE, recherche ET filtre
  // conservés. `total` et les compteurs d'en-tête viennent du serveur — jamais recalculés ici.
  reloadAfterPurge() {
    this.fetchPage(this.state.page, this.state.appliedQuery, this.state.activity);
  }

  closePurge() {
    this.setState({ purgeWebsiteId: null });
    this.reloadAfterPurge();
  }

  // Adresse déclarée sous le nom (C9) — l'API sert toujours `base_path` (C7 amendé, D93) : une clé
  // absente signe une API pas encore déployée → hôte en texte, `data-address-known="0"`. Un Google
  // Sites sans chemin connu n'a pas d'adresse utile → « adresse non déclarée » (D103). Lien externe
  // secondaire : le lien vers la fiche reste le nom (la ligne n'a pas d'`onClick`).
  renderAddress(site) {
    const addr = websiteAddress(site, { absentKey: 'unknown' });
    const sx = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
    const anchor = { 'data-testid': 'website-address', 'data-address-known': addr.known ? '1' : '0' };
    if (isUndeclaredGoogleSitesAddress(addr)) {
      return (
        <Tooltip title={`${addr.address} — chemin du site inconnu (adresse réduite à sites.google.com à la création)`}>
          <Typography variant="caption" color="text.disabled" sx={{ ...sx, fontStyle: 'italic' }} {...anchor} data-address-undeclared="1">
            adresse non déclarée
          </Typography>
        </Tooltip>
      );
    }
    if (!addr.known || !addr.address) {
      return (
        <Typography variant="caption" color="text.secondary" sx={sx} {...anchor}>
          {addr.address}
        </Typography>
      );
    }
    return (
      <MuiLink variant="caption" color="text.secondary" href={addr.address} target="_blank" rel="noopener" underline="hover" title={addr.address} sx={sx} {...anchor}>
        {addr.address}
      </MuiLink>
    );
  }

  renderHeader(nf) {
    const { total, lastFullAnalysisAt, remaining, loaded, activity } = this.state;
    const freshness = loaded ? hostingFreshness(lastFullAnalysisAt) : null;
    const remainingKnown = loaded && remaining !== undefined && remaining !== null;

    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Google Sites — suivi du parc</Typography>
          <Typography
            variant="body2"
            color={freshness && freshness.state === 'stale' ? 'warning.main' : 'text.secondary'}
            data-testid="hosting-detected-freshness"
            data-freshness={freshness ? freshness.state : 'loading'}
          >
            {!freshness && 'Chargement…'}
            {freshness && freshness.state === 'unknown' && (
              <Tooltip title="Date non servie par cette version de l’API (déploiement API en attente), ou illisible.">
                <span>Dernière analyse complète : —</span>
              </Tooltip>
            )}
            {freshness && freshness.state !== 'unknown' && (
              <Tooltip title={lastFullAnalysisAt ? fmtDate(lastFullAnalysisAt, true) : ''}>
                <span>{freshness.label}</span>
              </Tooltip>
            )}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap">
          <Chip
            color="primary"
            // Le total servi est celui du filtre courant : le libellé nomme ce qu'il compte.
            label={`${totalLabel(activity)} : ${loaded ? nf.format(total) : '…'}`}
            data-testid="hosting-detected-total"
          />
          <Tooltip title="Sites de TOUT le parc (tous hébergeurs, hors corbeille) que l’analyse n’a jamais regardés. Un lot « Lancer l’analyse » en traite jusqu’à 200.">
            <Chip
              variant="outlined"
              label={`${HEADER_REMAINING_LABEL} : ${remainingKnown ? nf.format(remaining) : '—'}`}
              data-testid="hosting-detected-remaining"
            />
          </Tooltip>
        </Stack>
      </Stack>
    );
  }

  renderBanners() {
    const { lastFullAnalysisAt, loaded } = this.state;
    const freshness = loaded ? hostingFreshness(lastFullAnalysisAt) : null;
    return (
      <>
        {freshness && freshness.state === 'stale' && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="hosting-detected-stale">
            Chiffres périmés : la dernière analyse complète du parc date de plus de {STALE_AFTER_DAYS} jours
            (il y a {freshness.days} j). Le plancher ne rajeunit qu’en rejouant les sites déjà analysés
            (case ci-dessous) ou par le script de rattrapage.
          </Alert>
        )}
        {freshness && freshness.state === 'never' && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="hosting-detected-never">
            Aucune analyse complète du parc à ce jour : la liste ne reflète que les lots déjà passés.
            Le premier balayage complet se fait par le script de rattrapage, pas par le bouton.
          </Alert>
        )}
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }} data-testid="hosting-detected-notice">
          Cumul historique : un site réparé ne sort jamais de cette liste — la colonne « Dernière cassée »
          dit si la panne est encore vivante (puce « ancien » au-delà de {OLD_BREAK_AFTER_DAYS} j). Les lignes grisées
          sont les sites inactifs.
          Les chiffres sont un plancher : seuls les sites déjà analysés et portant au moins une
          discussion cassée en base sont comptés.
        </Alert>
      </>
    );
  }

  renderAnalyze(nf) {
    const { onlyNew, analyzing, analyzeResult, analyzeMode, analyzeError } = this.state;
    const summary = analyzeResult ? analyzeSummary(analyzeResult, analyzeMode) : null;
    const err = describeAnalyzeError(analyzeError);
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="hosting-detected-analyze-block">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Button
            variant="contained"
            onClick={this.onAnalyze}
            disabled={analyzing}
            data-testid="hosting-detected-analyze"
          >
            Lancer l’analyse
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                checked={!onlyNew}
                onChange={(e) => this.setState({ onlyNew: !e.target.checked })}
                inputProps={{ 'data-testid': 'hosting-detected-replay' }}
              />
            }
            label="Rejouer aussi les sites déjà analysés (les plus anciens d’abord)"
          />
          <Typography variant="caption" color="text.secondary">
            Un clic = un lot borné (200 sites ou 25 s). Aucune boucle : relis les compteurs, reclique si besoin.
            Le premier balayage complet du parc se fait par script.
          </Typography>
        </Stack>
        {analyzing && (
          <Box sx={{ mt: 2 }} data-testid="hosting-detected-progress">
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">Analyse en cours…</Typography>
          </Box>
        )}
        {summary && !analyzing && (
          <Alert
            severity={summary.failed > 0 ? 'warning' : 'success'}
            sx={{ mt: 2 }}
            data-testid="hosting-detected-analyze-result"
            // Préfixe `batch-` : ces compteurs sont ceux du LOT (réponse du POST). Le conteneur porte
            // `data-remaining` = parc jamais analysé (GET) — deux grandeurs, deux noms d'attribut.
            data-batch-analyzed={String(summary.analyzed)}
            data-batch-detected={String(summary.detected)}
            data-batch-failed={String(summary.failed)}
            data-batch-remaining={summary.remaining === null ? '' : String(summary.remaining)}
          >
            Lot terminé : <strong>{nf.format(summary.analyzed)}</strong> site(s) analysé(s) avec succès,{' '}
            <strong>{nf.format(summary.detected)}</strong> constaté(s) Google Sites
            {summary.failed > 0 && (
              <>
                ,{' '}
                <Tooltip title={summary.failedIds.length ? `Identifiants (20 premiers au plus) : ${summary.failedIds.join(', ')}` : ''}>
                  <strong>{nf.format(summary.failed)}</strong>
                </Tooltip>{' '}
                en échec (non marqués analysés, ils seront retentés)
              </>
            )}
            . {ucfirst(summary.remainingLabel)} : <strong>{summary.remaining === null ? '—' : nf.format(summary.remaining)}</strong>.
          </Alert>
        )}
        {err && (
          <Alert severity={err.severity} sx={{ mt: 2 }} data-testid="hosting-detected-analyze-error" data-code={err.code}>
            {err.message}
          </Alert>
        )}
      </Paper>
    );
  }

  render() {
    const { sites, total, page, query, appliedQuery, isLoading, error, remaining, loaded, lastFullAnalysisAt, analyzeResult, analyzing, activity, inactiveAfterDays, purgeWebsiteId } = this.state;
    const filterAvailable = isActivityFilterAvailable(inactiveAfterDays);
    const nf = new Intl.NumberFormat('fr-FR');
    const state = screenState({ isLoading, error, sites, loaded });
    const pageCount = pageCountFor(total, PAGE_SIZE);
    const loadError = describeLoadError(error);
    const freshness = loaded ? hostingFreshness(lastFullAnalysisAt) : null;
    const now = new Date();

    return (
      <Box
        sx={{ p: 2 }}
        data-testid="hosting-detected-screen"
        // Attributs d'état en chiffres BRUTS (jamais Intl/toLocaleString : U+202F) — oracles de
        // recette, à ne lire QUE quand `data-state` a quitté `loading`.
        data-state={state}
        data-total={String(total)}
        data-rows={String(sites.length)}
        data-remaining={remaining === undefined || remaining === null ? undefined : String(remaining)}
        data-freshness={freshness ? freshness.state : 'loading'}
        data-activity={activity}
        data-activity-available={filterAvailable ? '1' : '0'}
        data-analyzing={analyzing ? '1' : '0'}
        data-analyzed={analyzeResult ? String(toCount(analyzeResult.analyzed)) : undefined}
      >
        {this.renderHeader(nf)}
        {this.renderBanners()}

        {loadError && (
          <Alert severity={loadError.severity} sx={{ mb: 2 }} data-testid="hosting-detected-error">
            {loadError.message}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={this.onSearchSubmit}
          sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <TextField
            size="small"
            label="Rechercher"
            placeholder="nom ou adresse du site"
            value={query}
            onChange={(e) => this.setState({ query: e.target.value })}
            inputProps={{ 'data-testid': 'hosting-detected-search' }}
            sx={{ minWidth: 280 }}
          />
          <Button type="submit" variant="contained" size="small" disabled={isLoading} data-testid="hosting-detected-search-submit">
            Rechercher
          </Button>
          {appliedQuery && (
            <Chip
              label={`Filtre : « ${appliedQuery} »`}
              onDelete={() => { this.setState({ query: '' }); this.fetchPage(1, '', activity); }}
              data-testid="hosting-detected-search-applied"
            />
          )}
          <Tooltip
            title={filterAvailable
              ? `Inactif = aucun commentaire depuis ${inactiveAfterDays} jours, ou jamais. Le filtre porte sur tout le parc, pas sur la page affichée. Un site pas encore re-mesuré n’est ni actif ni inactif.`
              : ''}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={activity}
              onChange={this.onActivityChange}
              disabled={!filterAvailable || isLoading}
              data-testid="hosting-detected-activity"
            >
              <ToggleButton value="all" data-testid="hosting-detected-activity-all">Tous</ToggleButton>
              <ToggleButton value="active" data-testid="hosting-detected-activity-active">Actifs</ToggleButton>
              <ToggleButton value="inactive" data-testid="hosting-detected-activity-inactive">Inactifs</ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
          {loaded && !filterAvailable && (
            <Typography variant="caption" color="text.secondary" data-testid="hosting-detected-activity-unavailable">
              Filtre d’activité indisponible : cette version de l’API ne sert pas encore le dernier commentaire des sites.
            </Typography>
          )}
        </Box>

        {this.renderAnalyze(nf)}

        <Paper elevation={1}>
          {isLoading && <LinearProgress data-testid="hosting-detected-loading" />}
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Site</TableCell>
                  <TableCell align="right">Discussions cassées</TableCell>
                  <TableCell align="right">Dont vides</TableCell>
                  <TableCell align="right">Commentaires piégés</TableCell>
                  <TableCell>Dernière cassée</TableCell>
                  <TableCell>
                    <Tooltip title="Date du dernier commentaire du site, tous statuts, relevée à la dernière analyse du site — pas en temps réel.">
                      <span>Dernier commentaire</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Constaté le</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {sites.map((row) => {
                  // Réponse IMBRIQUÉE : `row.website.*` / `row.hosting_detected.*` — jamais à plat.
                  const site = row.website || {};
                  const hd = row.hosting_detected || {};
                  const ratio = emptyRatio(hd.threads_empty, hd.threads_total);
                  const age = breakAge(hd.last_thread_at, now);
                  // `hd.last_comment_at` ABSENT (undefined) ≠ `null` : « non mesuré » ≠ « jamais ».
                  const act = googleSitesActivity(hd.last_comment_at, inactiveAfterDays, now);
                  // `row.purge` ABSENT = aucune suppression arrêtée (ou API d'avant) → `null`.
                  const halt = purgeHalt(row.purge);
                  // Une suppression arrêtée attend un geste : sa ligne n'est jamais estompée. Et le
                  // grisage épargne la cellule d'action — un bouton pâle se lit « désactivé ».
                  const dimmed = !halt && isRowDimmed(act.state, age.old);
                  return (
                    <TableRow
                      key={site._id}
                      hover
                      data-website-id={site._id}
                      data-activity-state={act.state}
                      data-old={age.old ? '1' : '0'}
                      data-dimmed={dimmed ? '1' : '0'}
                      data-purge-halt={halt ? halt.state : undefined}
                      sx={dimmed ? { '& > td:not([data-cell="actions"])': { opacity: 0.55 } } : undefined}
                    >
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography
                          variant="body2"
                          component={RouterLink}
                          to={`/super-admin/website/?website_id=${site._id}`}
                          sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {site.name || '(sans nom)'}
                        </Typography>
                        {this.renderAddress(site)}
                        {halt && (
                          <Chip size="small" color="warning" label={halt.label} sx={{ mt: 0.5 }} data-testid="hosting-detected-purge-halt" />
                        )}
                      </TableCell>
                      <TableCell align="right">{nf.format(toCount(hd.threads_total))}</TableCell>
                      <TableCell align="right">
                        {nf.format(toCount(hd.threads_empty))}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({ratio === null ? '—' : `${ratio} %`})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{nf.format(toCount(hd.comments_trapped))}</TableCell>
                      <TableCell>
                        <Tooltip title={hd.last_thread_at ? fmtDate(hd.last_thread_at, true) : ''}>
                          <span>{fmtDate(hd.last_thread_at)}</span>
                        </Tooltip>
                        {age.old && (
                          <Tooltip title={`Dernière discussion cassée il y a ${age.days} j (> ${OLD_BREAK_AFTER_DAYS} j) : panne probablement passée, ligne conservée (cumul).`}>
                            <Chip size="small" label="ancien" sx={{ ml: 1 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell data-testid="hosting-detected-last-comment">
                        {act.state === 'unmeasured' && (
                          <Tooltip title="Ce site n’a pas été re-mesuré depuis l’ajout du dernier commentaire : il n’est classé ni actif ni inactif. Un rejeu de l’analyse le remplit.">
                            <Typography component="span" variant="body2" color="text.secondary">non mesuré</Typography>
                          </Tooltip>
                        )}
                        {act.state !== 'unmeasured' && (
                          <Tooltip title={hd.last_comment_at ? fmtDate(hd.last_comment_at, true) : 'Aucun commentaire sur ce site.'}>
                            <span>{act.never ? 'jamais' : fmtDate(hd.last_comment_at)}</span>
                          </Tooltip>
                        )}
                        {act.label && (
                          <Chip
                            size="small"
                            color={act.state === 'active' ? 'success' : 'default'}
                            variant={act.state === 'active' ? 'filled' : 'outlined'}
                            label={act.never ? act.label : `${act.label} · ${act.ago}`}
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Date du PREMIER constat, jamais rafraîchie.">
                          <span>{fmtDate(hd.detected_at)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell padding="checkbox" data-cell="actions">
                        {canPurgeFromRow(act.state, row.purge) && (
                          <Tooltip title="Supprimer intégralement ce site">
                            <IconButton
                              size="small"
                              color="error"
                              aria-label="Supprimer intégralement ce site"
                              onClick={() => this.setState({ purgeWebsiteId: site._id })}
                              // ⛔ Pas `website-purge-open` : c'est l'ancre de la FICHE du site.
                              data-testid="hosting-detected-purge"
                            >
                              <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {sites.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" data-testid="hosting-detected-empty">
                        {isLoading
                          ? 'Chargement…'
                          : `Aucun site ${EMPTY_QUALIFIER[activity] || EMPTY_QUALIFIER.all}${appliedQuery ? ` pour « ${appliedQuery} »` : ''}.`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" data-testid="hosting-detected-page-label">
              Page {page} / {pageCount} · {PAGE_SIZE} lignes par page
            </Typography>
            <Pagination
              page={page}
              count={pageCount}
              onChange={this.onPageChange}
              size="small"
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={0}
              boundaryCount={1}
            />
          </Box>
        </Paper>

        {/* UNE fenêtre pour tout le tableau — jamais une par ligne (50 sondages de suivi). */}
        <WebsitePurgeDialog
          open={Boolean(purgeWebsiteId)}
          websiteId={purgeWebsiteId}
          onClose={this.closePurge}
          onPurged={this.reloadAfterPurge}
        />
      </Box>
    );
  }
}

export default HostingDetected;
